// src/pages/Admin.jsx - FULLY INTEGRATED (Option A)
import React, { useState, useEffect } from 'react';
import {
  Server,
  Activity,
  CheckCircle,
  XCircle,
  TrendingUp,
  Clock,
  Database,
  Zap,
  AlertTriangle,
  RefreshCw,
  Loader2,
  Info,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card.jsx';
import { Badge } from '@/components/ui/badge.jsx';
import {
  TableWrapper,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table.jsx';
import { apiCall } from '../utils/api';
import RecentAdminActivity from '../components/admin/RecentAdminActivity.jsx';

export default function Admin() {
  // State
  const [summaryMetrics, setSummaryMetrics] = useState({
    totalConnectors: 0,
    healthyConnectors: 0,
    failedConnectors: 0,
    syncJobsToday: 0,
  });
  const [connectorHealth, setConnectorHealth] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Mock data for placeholder sections
  const systemMetrics = [
    { label: 'API Uptime', value: '99.9%', trend: '+0.1%', status: 'up' },
    { label: 'Avg Response Time', value: '125ms', trend: '-15ms', status: 'up' },
    { label: 'Total API Calls', value: '1.2M', trend: '+12%', status: 'up' },
    { label: 'Error Rate', value: '0.3%', trend: '-0.1%', status: 'up' },
  ];



  useEffect(() => {
    loadAdminData();
  }, []);

  async function loadAdminData() {
    setLoading(true);
    setError(null);

    try {
      // 1. Load connector health (admin only)
      const healthRes = await apiCall('/admin/connector-health');
      
      if (healthRes.status === 403) {
        setError('Admin access required. Please contact your administrator.');
        setLoading(false);
        return;
      }

      if (!healthRes.ok) {
        throw new Error('Failed to load connector health');
      }

      const healthData = await healthRes.json();

      // 2. Load all connectors (for total count)
      const configsRes = await apiCall('/sync/configs');
      const configsData = await configsRes.json();

      // 3. Load sync runs (for "jobs today" count)
      const runsRes = await apiCall('/logs/sync-runs?limit=1000');
      const runsData = await runsRes.json();

      // Calculate summary metrics
      const totalConnectors = configsData.configs?.length || 0;
      const healthyConnectors = healthData.connectors?.filter(c => c.status === 'healthy').length || 0;
      const failedConnectors = healthData.connectors?.filter(c => c.status === 'error').length || 0;
      const syncJobsToday = runsData.runs?.filter(r => isToday(r.started_at)).length || 0;

      setSummaryMetrics({
        totalConnectors,
        healthyConnectors,
        failedConnectors,
        syncJobsToday,
      });

      // Transform connector health for table
      const connectors = (healthData.connectors || []).map(c => ({
        id: c.connector_config_id,
        name: c.display_name,
        status: mapStatus(c.status),
        lastSync: formatTimestamp(c.last_finished_at),
        rawStatus: c.last_status,
      }));

      setConnectorHealth(connectors);
    } catch (err) {
      console.error('Failed to load admin data:', err);
      setError(err.message || 'Failed to load admin data');
    } finally {
      setLoading(false);
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadAdminData();
    setTimeout(() => setRefreshing(false), 500);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            System Administration
          </h1>
        </div>
        <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-sm font-medium text-red-400">{error}</p>
            <p className="text-xs text-red-400/70 mt-1">
              Make sure you're logged in with an admin account
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white">
            System Administration
          </h1>
          <p className="text-sm text-[#a0a0a0] mt-1">
            Monitor connector health, system metrics, and administrative activity
          </p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#1a1a1a] border border-[#2a2a2a] text-white text-sm rounded-lg hover:bg-[#222222] transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <SummaryCard
          icon={Server}
          label="Total Connectors"
          value={summaryMetrics.totalConnectors}
          iconColor="text-blue-500"
        />
        <SummaryCard
          icon={CheckCircle}
          label="Healthy Connectors"
          value={summaryMetrics.healthyConnectors}
          iconColor="text-green-500"
        />
        <SummaryCard
          icon={XCircle}
          label="Failed Connectors"
          value={summaryMetrics.failedConnectors}
          iconColor="text-red-500"
        />
        <SummaryCard
          icon={Activity}
          label="Sync Jobs Today"
          value={summaryMetrics.syncJobsToday}
          iconColor="text-violet-500"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Connector Health - Takes 2 columns */}
        <div className="lg:col-span-2">
          <ConnectorHealthSection connectors={connectorHealth} />
        </div>

        {/* System Metrics - Takes 1 column */}
        <div>
          <SystemMetricsSection metrics={systemMetrics} />
        </div>
      </div>

      {/* Recent Activity */}
      <RecentAdminActivity  />
    </div>
  );
}

// ==========================================
// SUMMARY CARD
// ==========================================
function SummaryCard({ icon: Icon, label, value, iconColor }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-[#a0a0a0] uppercase tracking-wide">
              {label}
            </p>
            <p className="text-2xl font-semibold text-white mt-2">
              {value}
            </p>
          </div>
          <div className={`w-10 h-10 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg flex items-center justify-center flex-shrink-0`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// CONNECTOR HEALTH SECTION
// ==========================================
function ConnectorHealthSection({ connectors }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Connector Health</CardTitle>
          <Badge variant="neutral">
            {connectors.filter(c => c.status === 'healthy').length} / {connectors.length} healthy
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        {connectors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Server className="w-12 h-12 text-[#6a6a6a] mb-4" />
            <p className="text-sm font-medium text-white mb-1">
              No connectors found
            </p>
            <p className="text-sm text-[#6a6a6a]">
              Create a connector to see health metrics
            </p>
          </div>
        ) : (
          <TableWrapper>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connector</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Sync</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connectors.map((connector) => (
                  <TableRow key={connector.id}>
                    <TableCell className="font-medium">{connector.name}</TableCell>
                    <TableCell>
                      <HealthBadge status={connector.status} />
                    </TableCell>
                    <TableCell className="text-[#a0a0a0]">{connector.lastSync}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableWrapper>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// HEALTH BADGE
// ==========================================
function HealthBadge({ status }) {
  const config = {
    healthy: { variant: 'success', label: 'Healthy' },
    degraded: { variant: 'warning', label: 'Degraded' },
    down: { variant: 'error', label: 'Down' },
  };

  const { variant, label } = config[status] || config.degraded;
  return <Badge variant={variant}>{label}</Badge>;
}

// ==========================================
// SYSTEM METRICS SECTION (PLACEHOLDER)
// ==========================================
function SystemMetricsSection({ metrics }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>System Metrics</CardTitle>
          <Badge variant="neutral" className="bg-amber-500/10 text-amber-400 border-amber-500/20">
            Coming Soon
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-3 bg-amber-500/5 border border-amber-500/10 rounded-lg">
          <div className="flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-400/90">
              System-wide metrics endpoint is not yet available. These are placeholder values.
            </p>
          </div>
        </div>
        <div className="space-y-4 opacity-50">
          {metrics.map((metric, index) => (
            <MetricRow key={index} metric={metric} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ==========================================
// METRIC ROW
// ==========================================
function MetricRow({ metric }) {
  return (
    <div className="flex items-center justify-between py-3 border-b border-[#2a2a2a] last:border-0">
      <div className="flex-1">
        <p className="text-sm font-medium text-white">{metric.label}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-[#a0a0a0]">{metric.value}</span>
          <div className="flex items-center gap-1">
            <TrendingUp className={`w-3 h-3 ${metric.status === 'up' ? 'text-green-500' : 'text-red-500'}`} />
            <span className={`text-xs font-medium ${metric.status === 'up' ? 'text-green-500' : 'text-red-500'}`}>
              {metric.trend}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}


// ==========================================
// ACTIVITY ROW
// ==========================================
function ActivityRow({ item }) {

}
// ==========================================
// HELPER FUNCTIONS
// ==========================================

function mapStatus(backendStatus) {
  // Backend: "healthy" | "warning" | "error"
  // UI: "healthy" | "degraded" | "down"
  if (backendStatus === 'healthy') return 'healthy';
  if (backendStatus === 'error') return 'down';
  if (backendStatus === 'warning') return 'degraded';
  return 'degraded';
}

function formatTimestamp(isoString) {
  if (!isoString) return 'Never';
  
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hr${diffHours !== 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    
    return date.toLocaleDateString();
  } catch {
    return 'Unknown';
  }
}

function isToday(isoString) {
  if (!isoString) return false;
  
  try {
    const date = new Date(isoString);
    const today = new Date();
    
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  } catch {
    return false;
  }
}
